import { useEffect, useState } from "react";

import MyLocationIcon from "@mui/icons-material/MyLocation";
import { Box, CircularProgress, IconButton } from "@mui/material";
import {
  getPoliceServicesGroupPublic,
  PublicBulletinResource,
} from "@rubi/api-generated";
import { colors, toast, useMapColorScheme } from "@rubi/ui";
import {
  AdvancedMarker,
  Map,
  MapControl,
  Marker,
  Pin,
  useMap,
} from "@vis.gl/react-google-maps";
import { useSearchParams } from "react-router-dom";

import {
  getPublicAlertAddresses,
  PublicPoliceServicesGroupResponse,
} from "./types";

const IMPRECISE_RADIUS_METERS = 300;

function ImpreciseCircle({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    const circle = new google.maps.Circle({
      map,
      center: { lat, lng },
      radius: IMPRECISE_RADIUS_METERS,
      strokeColor: colors.primary.main,
      strokeOpacity: 0.6,
      strokeWeight: 2,
      fillColor: colors.primary.main,
      fillOpacity: 0.1,
      clickable: false,
    });
    return () => circle.setMap(null);
  }, [map, lat, lng]);

  return null;
}

const containerStyle = {
  width: "calc(100%)",
  height: "calc(100vh)",
  // marginLeft: "8px",
  // marginRight: "8px",
};

function PublicAlertMap({
  groupId,
  alerts,
  alertClickHandler,
  isLoading,
}: {
  groupId: number;
  alerts: PublicBulletinResource[];
  alertClickHandler: (alert: PublicBulletinResource, index: number) => void;
  isLoading: boolean;
}) {
  const mapColorScheme = useMapColorScheme();
  const googleMapId = "4380cd85df35e961";
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [usersLocation, setUsersLocation] =
    useState<google.maps.LatLngLiteral | null>(null);
  const [searchParams] = useSearchParams();
  const occurrence_number = searchParams.get("occurrence_number");
  const [selectedAlert, setSelectedAlert] =
    useState<PublicBulletinResource | null>(null);

  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral | null>(
    null,
  );
  const zoomFactor = 13;

  const map = useMap();

  useEffect(() => {
    getPoliceServicesGroupPublic({
      path: { group_id: groupId },
    }).then((res) => {
      const data = res.data as PublicPoliceServicesGroupResponse;
      setMapCenter(data.map_center ?? null);
    });
  }, []);

  useEffect(() => {
    if (occurrence_number) {
      const found = alerts.find(
        (alert) => alert.occurrence_number?.[0] === occurrence_number,
      );
      setSelectedAlert(found ?? null);
    }
  }, [occurrence_number, alerts]);

  const adjustMapBoundsToAlert = (
    alert: PublicBulletinResource,
    addressIndex?: number,
  ) => {
    const addresses = getPublicAlertAddresses(alert.location);
    const targetIndex =
      addressIndex !== undefined
        ? addressIndex
        : selectedAddressIndex !== null
          ? Number(selectedAddressIndex)
          : 0;
    const target =
      addresses?.[targetIndex] ??
      addresses?.find(
        (a) =>
          a.geometry &&
          typeof a.geometry.lat === "number" &&
          typeof a.geometry.lng === "number",
      );

    if (
      target?.geometry &&
      typeof target.geometry.lat === "number" &&
      typeof target.geometry.lng === "number"
    ) {
      map?.setCenter(target.geometry);
      map?.setZoom(zoomFactor + 4);
    }
  };

  useEffect(() => {
    if (isMapLoaded && selectedAlert === null) {
      if (mapCenter?.lat && mapCenter?.lng) {
        map?.setCenter(mapCenter);
        if ((map?.getZoom() ?? 0) < zoomFactor) {
          map?.setZoom(zoomFactor);
        }
      } else {
        getCurrentPosition();
      }
    }
  }, [isMapLoaded, selectedAlert, mapCenter]);

  useEffect(() => {
    if (selectedAlert && isMapLoaded) {
      adjustMapBoundsToAlert(selectedAlert);
    }
  }, [selectedAlert, isMapLoaded]);

  const getCurrentPosition = () => {
    // if browser refuse to share location, then show error.
    // otherwise, get the current position and set center of map to it.
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUsersLocation({ lat: latitude, lng: longitude });
        map?.setCenter({ lat: latitude, lng: longitude });
        if ((map?.getZoom() ?? 0) < zoomFactor) {
          map?.setZoom(zoomFactor);
        }
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          // 设置默认地图中心位置，不依赖用户信息
          const defaultLocation = { lat: 37.0902, lng: -95.7129 }; // 美国中心
          map?.setCenter(defaultLocation);
          if ((map?.getZoom() ?? 0) < zoomFactor) {
            map?.setZoom(zoomFactor);
          }
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          toast.error("Location unavailable. Please try again.");
        } else if (error.code === error.TIMEOUT) {
          toast.error("Location request timed out.");
        } else {
          toast.error("An unknown error occurred while fetching location.");
        }
      },
    );
  };

  /**
   *  Function to get the user's current location and pan to it
   */
  const panToCurrentLocation = () => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "granted") {
          getCurrentPosition();
        } else if (result.state === "denied") {
          toast.error(
            "Please enable location services in your browser and refresh the page to use this feature.",
          );
        } else if (result.state === "prompt") {
          getCurrentPosition();
        }
      });
    } else if (navigator.geolocation) {
      getCurrentPosition();
    }
  };

  const handleMarkerClick = (
    alert: PublicBulletinResource,
    index: number,
    addressIndex: number,
  ) => {
    alertClickHandler(alert, index);
    adjustMapBoundsToAlert(alert, addressIndex);
  };

  const selectedAddressIndex = searchParams.get("selectedAddressIndex");

  const getMarkers = (alerts: PublicBulletinResource[]) =>
    alerts.flatMap((alert, index) =>
      getPublicAlertAddresses(alert.location).map((address, addressIndex) => {
        const { lat, lng } = address.geometry || {};
        if (typeof lat !== "number" || typeof lng !== "number") return null;

        const isSelected =
          selectedAlert &&
          alert.occurrence_number?.[0] ===
            selectedAlert.occurrence_number?.[0] &&
          (selectedAddressIndex === null ||
            addressIndex === Number(selectedAddressIndex));

        return [
          <AdvancedMarker
            key={`${index}-${addressIndex}`}
            position={{ lat, lng }}
            onClick={() => {
              handleMarkerClick(alert, index, addressIndex);
            }}
            clickable
          >
            <Pin
              background={isSelected ? colors.primary.main : undefined}
              borderColor={isSelected ? colors.info.dark : undefined}
              glyphColor={isSelected ? colors.primary.contrastText : undefined}
              scale={isSelected ? 1.3 : 1}
            />
          </AdvancedMarker>,
          address.precise === false && (
            <ImpreciseCircle
              key={`${index}-${addressIndex}-circle`}
              lat={lat}
              lng={lng}
            />
          ),
        ];
      }),
    );

  return (
    <div style={containerStyle}>
      <Map
        colorScheme={mapColorScheme}
        onTilesLoaded={() => {
          setIsMapLoaded(true);
        }}
        mapId={googleMapId}
        defaultZoom={zoomFactor}
        defaultCenter={mapCenter ?? { lat: 37.0902, lng: -95.7129 }}
        minZoom={3}
        fullscreenControl={false}
        streetViewControl={false}
      >
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {isMapLoaded && getMarkers(alerts)}

        {isMapLoaded && (
          <MapControl position={google.maps.ControlPosition.RIGHT_BOTTOM}>
            <Box
              sx={{
                bgcolor: "background.paper",
                boxShadow: 2,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                margin: "10px",
              }}
            >
              <IconButton
                onClick={panToCurrentLocation}
                aria-label="Pan to current location"
              >
                <MyLocationIcon />
              </IconButton>
            </Box>
          </MapControl>
        )}
        {isMapLoaded && usersLocation?.lat && usersLocation?.lng && (
          <Marker
            position={usersLocation}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: colors.primary.main,
              fillOpacity: 1,
              strokeColor: colors.primary.contrastText,
              strokeWeight: 2,
            }}
            onClick={panToCurrentLocation}
            zIndex={1000}
          />
        )}
      </Map>
    </div>
  );
}

export default PublicAlertMap;
